<template>
  <div ref="pageRoot" class="clubs-map-page">
    <section class="clubs-map-shell">
      <div
        ref="mapViewport"
        class="clubs-map-canvas"
        :class="{ 'is-dragging': isDragging }"
        @pointerdown="onMapPointerDown"
        @wheel.prevent="onMapWheel"
        @touchstart="onMapTouchStart"
      >
        <div class="clubs-map-toolbar glass">
          <div class="clubs-map-toolbar__row clubs-map-toolbar__row--top">
            <div class="clubs-city-picker">
              <span class="clubs-city-picker__icon">
                <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path
                    d="M12 21s6-5.38 6-11a6 6 0 1 0-12 0c0 5.62 6 11 6 11Z"
                    stroke="currentColor"
                    stroke-width="1.8"
                    stroke-linejoin="round"
                  />
                  <circle cx="12" cy="10" r="2.5" fill="currentColor" />
                </svg>
              </span>

              <button
                type="button"
                class="clubs-city-picker__control"
                @click="toggleCityPanel"
              >
                {{ selectedCity || 'Все города' }}
              </button>
            </div>

            <button
              type="button"
              class="clubs-map-toolbar__action"
              :disabled="locatingUser"
              @click="locateUser"
            >
              <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="m21 3-8.8 18-1.95-7.25L3 11.8 21 3Z" fill="currentColor" />
              </svg>
            </button>
          </div>

          <div v-if="showCityPanel" class="clubs-city-panel glass">
            <div class="clubs-city-panel__head">
              <input
                ref="citySearchInput"
                v-model.trim="citySearchQuery"
                type="text"
                class="clubs-city-panel__input"
                placeholder="Поиск города"
              />
              <button
                type="button"
                class="clubs-city-panel__close"
                @click="closeCityPanel"
              >
                ×
              </button>
            </div>

            <div class="clubs-city-panel__list">
              <button
                v-for="city in filteredCityOptions"
                :key="city"
                type="button"
                class="clubs-city-panel__item"
                :class="{ 'is-active': city === selectedCity }"
                @click="selectCity(city)"
              >
                <strong>{{ city }}</strong>
                <span>{{ clubsCountByCity(city) }} {{ clubWord(clubsCountByCity(city)) }}</span>
              </button>

              <div v-if="!filteredCityOptions.length" class="clubs-city-panel__empty">
                Город не найден
              </div>
            </div>
          </div>
        </div>

        <div v-if="loading" class="clubs-map-state glass">
          Загружаем карту клубов...
        </div>

        <div v-else-if="error" class="clubs-map-state clubs-map-state--error glass">
          {{ error }}
        </div>

        <div v-if="geolocationError" class="clubs-geo-message glass">
          {{ geolocationError }}
        </div>

        <template v-if="!loading && !error">
          <div class="clubs-map-canvas__tiles">
            <img
              v-for="tile in visibleTiles"
              :key="tile.key"
              class="clubs-map-canvas__tile"
              :src="tile.url"
              alt=""
              draggable="false"
              :style="{
                width: TILE_SIZE + 'px',
                height: TILE_SIZE + 'px',
                transform: `translate3d(${tile.left}px, ${tile.top}px, 0)`,
              }"
            />
          </div>

          <div class="clubs-map-canvas__overlay"></div>

          <button
            type="button"
            class="clubs-map-search-button"
            @click.stop="toggleSearchPanel"
          >
            <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <circle cx="11" cy="11" r="6" stroke="currentColor" stroke-width="2" />
              <path d="m16 16 4 4" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
            </svg>
          </button>

          <div v-if="showSearchPanel" class="clubs-map-search-panel glass">
            <input
              ref="searchInput"
              v-model.trim="searchQuery"
              type="text"
              class="clubs-map-search-panel__input"
              placeholder="Название клуба или адрес"
            />

            <div class="clubs-map-search-panel__list">
              <button
                v-for="club in filteredClubs"
                :key="club.id"
                type="button"
                class="clubs-map-search-panel__item"
                @click="selectClubOnMap(club, { focus: true, closeSearch: true })"
              >
                <strong>{{ club.name }}</strong>
                <span>{{ club.address || club.city || 'Адрес уточняется' }}</span>
              </button>

              <div v-if="!filteredClubs.length" class="clubs-map-search-panel__empty">
                Ничего не найдено
              </div>
            </div>
          </div>

          <button
            v-for="marker in displayMarkers"
            :key="marker.key"
            type="button"
            class="club-map-marker"
            :class="[
              marker.type === 'cluster' ? 'is-cluster' : 'is-club',
              {
                'is-selected': marker.type === 'club' && Number(selectedClubId) === Number(marker.club.id),
                'is-current': marker.type === 'club' && isCurrentClub(marker.club),
              },
            ]"
            :style="{ transform: `translate3d(${marker.left}px, ${marker.top}px, 0)` }"
            @click.stop="openMarker(marker)"
          >
            <template v-if="marker.type === 'cluster'">
              <span class="club-map-marker__cluster-ring"></span>
              <span class="club-map-marker__cluster-bubble">
                {{ marker.count }}
              </span>
            </template>

            <template v-else>
              <span class="club-map-marker__pulse"></span>

              <span class="club-map-marker__bubble">
                <template v-if="marker.club.logo_url || marker.club.image_url">
                  <img
                    :src="marker.club.logo_url || marker.club.image_url"
                    :alt="marker.club.name"
                    class="club-map-marker__image"
                  />
                </template>
                <template v-else>
                  <span class="club-map-marker__text">{{ clubMonogram(marker.club) }}</span>
                </template>
              </span>
            </template>
          </button>

          <div
            v-if="userLocationMarker"
            class="user-location-marker"
            :style="{ transform: `translate3d(${userLocationMarker.left}px, ${userLocationMarker.top}px, 0)` }"
          >
            <span class="user-location-marker__pulse"></span>
            <span class="user-location-marker__dot"></span>
          </div>

          <div class="clubs-map-zoom">
            <button type="button" class="clubs-map-zoom__button" @click.stop="zoomIn">+</button>
            <button type="button" class="clubs-map-zoom__button" @click.stop="zoomOut">−</button>
          </div>

          <transition name="club-mini-popup">
            <button
              v-if="selectedClub"
              type="button"
              class="club-mini-popup glass"
              @click="openSelectedClubPage"
            >
              <div class="club-mini-popup__media">
                <img
                  v-if="selectedClub.image_url"
                  :src="selectedClub.image_url"
                  :alt="selectedClub.name"
                  class="club-mini-popup__image"
                />
                <div v-else class="club-mini-popup__image club-mini-popup__image--placeholder">
                  <span>{{ clubMonogram(selectedClub) }}</span>
                </div>
              </div>

              <div class="club-mini-popup__body">
                <div class="club-mini-popup__top">
                  <strong>{{ selectedClub.name }}</strong>
                  <!--<span v-if="selectedClub.min_price_from" class="club-mini-popup__price">
                    от {{ money(selectedClub.min_price_from) }}
                  </span>-->
                </div>

                <div class="club-mini-popup__address">
                  {{ selectedClub.address || 'Адрес уточняется' }}
                </div>

                <div class="club-mini-popup__meta">
                  <span v-if="displayRating(selectedClub)">★ {{ displayRating(selectedClub) }}</span>
                  <span v-else>Нет оценок</span>
                  <template v-if="Number(selectedClub.reviews_count || 0) > 0">
                    <span>· от {{ money(selectedClub.min_price_from) }}</span>
                  </template>
                  <span v-if="selectedClub.city">· {{ selectedClub.city }}</span>
                </div>
              </div>

              <span class="club-mini-popup__arrow">
                <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path d="m9 5 7 7-7 7" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </span>
            </button>
          </transition>

          <div
            class="clubs-bottom-sheet glass"
            :class="{ 'is-open': sheetOpened }"
            :style="sheetStyle"
          >
            <div
              class="clubs-bottom-sheet__handle"
              @pointerdown.stop.prevent="onSheetHandlePointerDown"
              @touchstart.stop.prevent="onSheetHandleTouchStart"
            >
              <span></span>
            </div>

            <template v-if="sheetOpened">
              <div class="clubs-bottom-sheet__header">
                <div class="clubs-bottom-sheet__header-main">
                  <h2>{{ selectedCity || 'Все города' }}</h2>
                  <p>{{ foundCountLabel }}</p>
                </div>
              </div>

              <div class="clubs-bottom-sheet__list">
                <button
                  v-for="club in sheetClubs"
                  :key="club.id"
                  type="button"
                  class="clubs-bottom-sheet__club"
                  :class="{ 'is-selected': Number(selectedClubId) === Number(club.id) }"
                  @click="selectClubOnMap(club, { focus: true })"
                >
                  <div class="clubs-bottom-sheet__club-media">
                    <img
                      v-if="club.image_url"
                      :src="club.image_url"
                      :alt="club.name"
                      class="clubs-bottom-sheet__club-image"
                    />
                    <div v-else class="clubs-bottom-sheet__club-image clubs-bottom-sheet__club-image--placeholder">
                      <span>{{ clubMonogram(club) }}</span>
                    </div>
                  </div>

                  <div class="clubs-bottom-sheet__club-body">
                    <div class="clubs-bottom-sheet__club-top">
                      <strong>{{ club.name }}</strong>
                      <span v-if="club.min_price_from" class="clubs-bottom-sheet__club-price">
                        от {{ money(club.min_price_from) }}
                      </span>
                    </div>

                    <div class="clubs-bottom-sheet__club-address">
                      {{ club.address || 'Адрес уточняется' }}
                    </div>

                    <div class="clubs-bottom-sheet__club-meta">
                      <span v-if="displayRating(club)">★ {{ displayRating(club) }}</span>
                      <span v-else>Нет оценок</span>
                      <template v-if="Number(club.reviews_count || 0) > 0">
                        <span>· {{ club.reviews_count }}</span>
                      </template>
                    </div>
                  </div>
                </button>

                <div v-if="!sheetClubs.length" class="clubs-bottom-sheet__empty">
                  В этом городе клубы не найдены
                </div>
              </div>
            </template>

            <template v-else>
              <button
                type="button"
                class="clubs-bottom-sheet__summary"
                @click="expandSheetFromTap"
              >
                <div class="clubs-bottom-sheet__summary-text">
                  <strong>{{ selectedCity || 'Все города' }}</strong>
                  <span>{{ foundCountLabel }}</span>
                </div>
              </button>
            </template>
          </div>
        </template>
      </div>
    </section>
  </div>
</template>

<script>
const TILE_SIZE = 256;
const MIN_LATITUDE = -85.05112878;
const MAX_LATITUDE = 85.05112878;

const CITY_COORDINATES = {
  'Москва': { latitude: 55.7558, longitude: 37.6173 },
  'Санкт-Петербург': { latitude: 59.9343, longitude: 30.3351 },
  'Астрахань': { latitude: 46.3497, longitude: 48.0408 },
  'Казань': { latitude: 55.7961, longitude: 49.1064 },
  'Екатеринбург': { latitude: 56.8389, longitude: 60.6057 },
  'Новосибирск': { latitude: 55.0084, longitude: 82.9357 },
  'Краснодар': { latitude: 45.0355, longitude: 38.9753 },
  'Сочи': { latitude: 43.5855, longitude: 39.7231 },
  'Ростов-на-Дону': { latitude: 47.2357, longitude: 39.7015 },
  'Волгоград': { latitude: 48.708, longitude: 44.5133 },
  'Самара': { latitude: 53.1959, longitude: 50.1008 },
  'Нижний Новгород': { latitude: 56.2965, longitude: 43.9361 },
  'Воронеж': { latitude: 51.6608, longitude: 39.2003 },
  'Уфа': { latitude: 54.7388, longitude: 55.9721 },
  'Пермь': { latitude: 58.0105, longitude: 56.2502 },
  'Омск': { latitude: 54.9885, longitude: 73.3242 },
  'Челябинск': { latitude: 55.1644, longitude: 61.4368 },
  'Тюмень': { latitude: 57.153, longitude: 65.5343 },
  'Калининград': { latitude: 54.7104, longitude: 20.4522 },
};

function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

function normalizeLongitude(longitude) {
  let next = Number(longitude || 0);
  while (next < -180) next += 360;
  while (next > 180) next -= 360;
  return next;
}

function toNumber(value, fallback = null) {
  if (value === null || value === undefined || value === '') {
    return fallback;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

export default {
  name: 'ClubsView',

  data() {
    return {
      TILE_SIZE,
      selectedCity: '',
      citySearchQuery: '',
      searchQuery: '',
      selectedClubId: null,
      showCityPanel: false,
      showSearchPanel: false,

      mapWidth: 0,
      mapHeight: 0,
      centerLatitude: 55.7558,
      centerLongitude: 37.6173,
      zoom: 4,
      minZoom: 3,
      maxZoom: 17,

      isDragging: false,
      dragStart: null,
      resizeObserver: null,
      hasInitializedViewport: false,

      locatingUser: false,
      userLocation: null,
      geolocationError: '',

      touchActive: false,
      touchGesture: null,

      layoutLockElements: [],

      sheetOpened: false,
      sheetMinHeightPx: 92,
      sheetMaxHeightPx: 520,
      sheetHeightPx: 92,
      sheetDragging: false,
      sheetDragStartY: 0,
      sheetStartHeightPx: 92,
      sheetDragMoved: false,
    };
  },

  computed: {
    clubs() {
      return this.$store.getters['clubs/items'] || [];
    },

    loading() {
      return this.$store.getters['clubs/loading'] || false;
    },

    error() {
      return this.$store.getters['clubs/error'] || '';
    },

    currentClub() {
      return this.$store.getters['clubs/currentClub'] || null;
    },

    selectedClub() {
      return this.clubs.find((club) => Number(club.id) === Number(this.selectedClubId)) || null;
    },

    cityOptions() {
      const values = new Set();
      this.clubs.forEach((club) => {
        const city = String(club?.city || '').trim();
        if (city) values.add(city);
      });
      return Array.from(values).sort((a, b) => a.localeCompare(b, 'ru'));
    },

    filteredCityOptions() {
      const query = String(this.citySearchQuery || '').trim().toLowerCase();
      if (!query) return this.cityOptions;
      return this.cityOptions.filter((city) => city.toLowerCase().includes(query));
    },

    filteredClubs() {
      const query = String(this.searchQuery || '').trim().toLowerCase();
      const selectedCity = String(this.selectedCity || '').trim().toLowerCase();

      return this.clubs.filter((club) => {
        const clubCity = String(club?.city || '').trim().toLowerCase();
        const text = [club?.name, club?.address, club?.city]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();

        const okCity = !selectedCity || clubCity === selectedCity;
        const okQuery = !query || text.includes(query);

        return okCity && okQuery;
      });
    },

    sheetClubs() {
      if (!this.selectedCity) {
        return this.clubs;
      }

      return this.clubs.filter(
        (club) => String(club?.city || '').trim().toLowerCase() === String(this.selectedCity || '').trim().toLowerCase(),
      );
    },

    foundCountLabel() {
      const count = this.sheetClubs.length;
      return `Найдено ${count} ${this.clubWord(count)}`;
    },

    sheetStyle() {
      return {
        height: `${this.sheetOpened ? this.sheetHeightPx : this.sheetMinHeightPx}px`,
      };
    },

    mapReadyClubs() {
      return this.clubs
        .map((club) => ({ club, point: this.resolveClubPoint(club) }))
        .filter((item) => item.point);
    },

    visibleTiles() {
      if (!this.mapWidth || !this.mapHeight) return [];

      const zoom = clamp(Math.round(this.zoom), this.minZoom, this.maxZoom);
      const worldCenter = this.project(this.centerLatitude, this.centerLongitude, zoom);
      const leftEdge = worldCenter.x - this.mapWidth / 2;
      const topEdge = worldCenter.y - this.mapHeight / 2;
      const firstTileX = Math.floor(leftEdge / TILE_SIZE);
      const firstTileY = Math.floor(topEdge / TILE_SIZE);
      const lastTileX = Math.floor((leftEdge + this.mapWidth) / TILE_SIZE);
      const lastTileY = Math.floor((topEdge + this.mapHeight) / TILE_SIZE);
      const maxTile = 2 ** zoom;
      const tiles = [];

      for (let tileX = firstTileX - 1; tileX <= lastTileX + 1; tileX += 1) {
        for (let tileY = firstTileY - 1; tileY <= lastTileY + 1; tileY += 1) {
          if (tileY < 0 || tileY >= maxTile) continue;

          const wrappedX = ((tileX % maxTile) + maxTile) % maxTile;

          tiles.push({
            key: `${zoom}-${wrappedX}-${tileY}-${tileX}`,
            left: tileX * TILE_SIZE - leftEdge,
            top: tileY * TILE_SIZE - topEdge,
            url: `https://basemaps.cartocdn.com/dark_all/${zoom}/${wrappedX}/${tileY}.png`,
          });
        }
      }

      return tiles;
    },

    displayMarkers() {
      if (!this.mapWidth || !this.mapHeight) return [];

      const zoom = clamp(Math.round(this.zoom), this.minZoom, this.maxZoom);
      const centerPoint = this.project(this.centerLatitude, this.centerLongitude, zoom);

      const worldItems = this.mapReadyClubs.map(({ club, point }) => {
        const worldPoint = this.project(point.latitude, point.longitude, zoom);
        return { club, point, worldPoint };
      });

      const markers = [];
      const shouldCluster = zoom <= 10;

      if (shouldCluster) {
        const gridSize = zoom <= 5 ? 110 : zoom <= 7 ? 96 : 80;
        const groups = new Map();

        worldItems.forEach((item) => {
          const key = `${Math.floor(item.worldPoint.x / gridSize)}:${Math.floor(item.worldPoint.y / gridSize)}`;
          if (!groups.has(key)) groups.set(key, []);
          groups.get(key).push(item);
        });

        groups.forEach((items, key) => {
          if (items.length === 1) {
            const item = items[0];
            markers.push({
              key: `club-${item.club.id}`,
              type: 'club',
              club: item.club,
              left: item.worldPoint.x - centerPoint.x + this.mapWidth / 2,
              top: item.worldPoint.y - centerPoint.y + this.mapHeight / 2,
            });
            return;
          }

          const avgX = items.reduce((sum, item) => sum + item.worldPoint.x, 0) / items.length;
          const avgY = items.reduce((sum, item) => sum + item.worldPoint.y, 0) / items.length;
          const avgLat = items.reduce((sum, item) => sum + item.point.latitude, 0) / items.length;
          const avgLng = items.reduce((sum, item) => sum + item.point.longitude, 0) / items.length;

          markers.push({
            key: `cluster-${key}`,
            type: 'cluster',
            count: items.length,
            latitude: avgLat,
            longitude: avgLng,
            left: avgX - centerPoint.x + this.mapWidth / 2,
            top: avgY - centerPoint.y + this.mapHeight / 2,
          });
        });
      } else {
        const grouped = new Map();

        worldItems.forEach((item) => {
          const key = this.getMarkerGroupKey(item.point);
          if (!grouped.has(key)) grouped.set(key, []);
          grouped.get(key).push(item);
        });

        grouped.forEach((items) => {
          items.forEach((item, index) => {
            const spread = this.getMarkerSpreadOffset(index, items.length);
            markers.push({
              key: `club-${item.club.id}`,
              type: 'club',
              club: item.club,
              left: item.worldPoint.x - centerPoint.x + this.mapWidth / 2 + spread.x,
              top: item.worldPoint.y - centerPoint.y + this.mapHeight / 2 + spread.y,
            });
          });
        });
      }

      return markers.filter((item) => (
        item.left >= -100 &&
        item.left <= this.mapWidth + 100 &&
        item.top >= -100 &&
        item.top <= this.mapHeight + 100
      ));
    },

    userLocationMarker() {
      if (!this.userLocation || !this.mapWidth || !this.mapHeight) return null;

      const zoom = clamp(Math.round(this.zoom), this.minZoom, this.maxZoom);
      const centerPoint = this.project(this.centerLatitude, this.centerLongitude, zoom);
      const worldPoint = this.project(this.userLocation.latitude, this.userLocation.longitude, zoom);

      return {
        left: worldPoint.x - centerPoint.x + this.mapWidth / 2,
        top: worldPoint.y - centerPoint.y + this.mapHeight / 2,
      };
    },
  },

  watch: {
    clubs: {
      deep: true,
      handler(clubs) {
        if (!Array.isArray(clubs) || !clubs.length) return;

        if (!this.selectedCity) {
          this.selectedCity = this.currentClub?.city || clubs[0]?.city || '';
        }

        if (!this.selectedClubId) {
          const initialClub = this.currentClub || clubs[0] || null;
          this.selectedClubId = initialClub ? Number(initialClub.id) : null;
        }

        this.$nextTick(() => {
          this.measureMap();

          if (!this.hasInitializedViewport) {
            this.fitToClubs(this.clubs.filter((club) => this.resolveClubPoint(club)));
            this.hasInitializedViewport = true;
          }
        });
      },
    },

    selectedCity() {
      const cityClubs = this.selectedCity
        ? this.clubs.filter((club) => String(club?.city || '').trim().toLowerCase() === String(this.selectedCity || '').trim().toLowerCase())
        : this.clubs;

      const clubsWithPoints = cityClubs.filter((club) => this.resolveClubPoint(club));
      if (clubsWithPoints.length) {
        this.fitToClubs(clubsWithPoints);
      }

      this.closeCityPanel();
    },
  },

  async mounted() {
    this.applyLayoutLocks();
    this.syncViewportHeightVar();

    await this.$store.dispatch('clubs/fetchClubs');

    this.$nextTick(() => {
      this.measureMap();
      this.setupResizeObserver();
      this.updateSheetBounds();

      window.addEventListener('pointermove', this.onGlobalPointerMove);
      window.addEventListener('pointerup', this.onGlobalPointerUp);
      window.addEventListener('pointercancel', this.onGlobalPointerUp);

      window.addEventListener('resize', this.onWindowResize);
      window.addEventListener('orientationchange', this.onWindowResize);

      if (window.visualViewport) {
        window.visualViewport.addEventListener('resize', this.onViewportResize);
        window.visualViewport.addEventListener('scroll', this.onViewportResize);
      }

      window.addEventListener('touchmove', this.onGlobalTouchMove, { passive: false });
      window.addEventListener('touchend', this.onGlobalTouchEnd, { passive: false });
      window.addEventListener('touchcancel', this.onGlobalTouchEnd, { passive: false });
    });
  },

  beforeUnmount() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }

    window.removeEventListener('pointermove', this.onGlobalPointerMove);
    window.removeEventListener('pointerup', this.onGlobalPointerUp);
    window.removeEventListener('pointercancel', this.onGlobalPointerUp);
    window.removeEventListener('resize', this.onWindowResize);
    window.removeEventListener('orientationchange', this.onWindowResize);

    if (window.visualViewport) {
      window.visualViewport.removeEventListener('resize', this.onViewportResize);
      window.visualViewport.removeEventListener('scroll', this.onViewportResize);
    }

    window.removeEventListener('touchmove', this.onGlobalTouchMove);
    window.removeEventListener('touchend', this.onGlobalTouchEnd);
    window.removeEventListener('touchcancel', this.onGlobalTouchEnd);

    this.removeLayoutLocks();
  },

  methods: {
    applyLayoutLocks() {
      document.documentElement.classList.add('clubs-map-no-scroll');
      document.body.classList.add('clubs-map-no-scroll');

      const elements = [];
      let node = this.$refs.pageRoot;

      while (node && node.parentElement) {
        node = node.parentElement;
        const cls = typeof node.className === 'string' ? node.className : '';

        if (
          cls.includes('app-shell') ||
          cls.includes('app-page') ||
          cls.includes('app-content') ||
          cls.includes('page-content')
        ) {
          node.classList.add('clubs-map-fullbleed');
          elements.push(node);
        }
      }

      this.layoutLockElements = elements;
    },

    removeLayoutLocks() {
      document.documentElement.classList.remove('clubs-map-no-scroll');
      document.body.classList.remove('clubs-map-no-scroll');

      (this.layoutLockElements || []).forEach((el) => {
        el.classList.remove('clubs-map-fullbleed');
      });

      this.layoutLockElements = [];
    },

    syncViewportHeightVar() {
      const height = Math.round(window.visualViewport?.height || window.innerHeight || 0);
      if (this.$refs.pageRoot && height) {
        this.$refs.pageRoot.style.setProperty('--clubs-vh', `${height}px`);
      }
    },

    onViewportResize() {
      this.syncViewportHeightVar();
      this.measureMap();
      this.updateSheetBounds();
    },

    onWindowResize() {
      this.syncViewportHeightVar();
      this.measureMap();
      this.updateSheetBounds();
    },

    clubWord(count) {
      const mod10 = count % 10;
      const mod100 = count % 100;
      if (mod10 === 1 && mod100 !== 11) return 'клуб';
      if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return 'клуба';
      return 'клубов';
    },

    clubsCountByCity(city) {
      return this.clubs.filter(
        (club) => String(club?.city || '').trim().toLowerCase() === String(city || '').trim().toLowerCase(),
      ).length;
    },

    setupResizeObserver() {
      if (!window.ResizeObserver || !this.$refs.mapViewport) return;
      this.resizeObserver = new ResizeObserver(() => this.measureMap());
      this.resizeObserver.observe(this.$refs.mapViewport);
    },

    measureMap() {
      const element = this.$refs.mapViewport;
      if (!element) return;
      this.mapWidth = Math.max(element.clientWidth || 0, 320);
      this.mapHeight = Math.max(element.clientHeight || 0, 320);
      this.updateSheetBounds();
    },

    getBottomNavigationOffset() {
      return window.innerWidth < 1100 ? 88 : 18;
    },

    updateSheetBounds() {
      const viewportHeight = Math.round(window.visualViewport?.height || window.innerHeight || 800);
      const bottomOffset = this.getBottomNavigationOffset();
      const maxByViewport = Math.floor(viewportHeight * 0.72) - bottomOffset - 108;
      const maxByMap = this.mapHeight - bottomOffset - 120;

      this.sheetMaxHeightPx = Math.max(260, Math.min(maxByViewport, maxByMap));

      if (this.sheetOpened) {
        const nextHeight = this.sheetHeightPx <= this.sheetMinHeightPx
          ? this.getDefaultOpenSheetHeight()
          : this.sheetHeightPx;

        this.sheetHeightPx = clamp(nextHeight, this.sheetMinHeightPx, this.sheetMaxHeightPx);
      } else {
        this.sheetHeightPx = this.sheetMinHeightPx;
      }
    },

    getDefaultOpenSheetHeight() {
      return clamp(Math.round(this.mapHeight * 0.42), 280, this.sheetMaxHeightPx);
    },

    collapseSheet() {
      this.sheetOpened = false;
      this.sheetHeightPx = this.sheetMinHeightPx;
    },

    expandSheetFromTap() {
      this.sheetOpened = true;
      this.sheetHeightPx = this.getDefaultOpenSheetHeight();
    },

    onSheetHandlePointerDown(event) {
      if (this.touchActive) return;
      this.sheetDragging = true;
      this.sheetDragStartY = event.clientY;
      this.sheetStartHeightPx = this.sheetOpened ? this.sheetHeightPx : this.sheetMinHeightPx;
      this.sheetDragMoved = false;
    },

    onSheetHandleTouchStart(event) {
      const touch = event.touches?.[0];
      if (!touch) return;

      this.touchActive = true;
      this.sheetDragging = true;
      this.sheetDragStartY = touch.clientY;
      this.sheetStartHeightPx = this.sheetOpened ? this.sheetHeightPx : this.sheetMinHeightPx;
      this.sheetDragMoved = false;
    },

    finalizeSheetDrag() {
      if (!this.sheetDragMoved) {
        this.sheetDragging = false;
        this.sheetDragMoved = false;
        return;
      }

      if (this.sheetHeightPx <= this.sheetMinHeightPx + 38) {
        this.sheetOpened = false;
        this.sheetHeightPx = this.sheetMinHeightPx;
      } else {
        this.sheetOpened = true;
        this.sheetHeightPx = clamp(this.sheetHeightPx, this.sheetMinHeightPx, this.sheetMaxHeightPx);
      }

      this.sheetDragging = false;
      this.sheetDragMoved = false;
    },

    async locateUser() {
      this.geolocationError = '';

      if (this.locatingUser) return;

      if (!window.isSecureContext) {
        this.geolocationError = 'Геолокация работает только по HTTPS или localhost.';
        return;
      }

      if (!navigator.geolocation) {
        this.geolocationError = 'В этом браузере геолокация недоступна.';
        return;
      }

      this.locatingUser = true;

      navigator.geolocation.getCurrentPosition(
        (position) => {
          const latitude = clamp(position.coords.latitude, MIN_LATITUDE, MAX_LATITUDE);
          const longitude = normalizeLongitude(position.coords.longitude);

          this.userLocation = { latitude, longitude };
          this.centerLatitude = latitude;
          this.centerLongitude = longitude;
          this.zoom = Math.max(15, this.zoom);
          this.locatingUser = false;
        },
        (error) => {
          if (error?.code === 1) {
            this.geolocationError = 'Доступ к геолокации запрещён. Разреши его в Safari или в настройках iPhone.';
          } else if (error?.code === 2) {
            this.geolocationError = 'Не удалось определить местоположение.';
          } else if (error?.code === 3) {
            this.geolocationError = 'Истекло время ожидания геолокации.';
          } else {
            this.geolocationError = 'Не удалось получить геолокацию.';
          }
          this.locatingUser = false;
        },
        { enableHighAccuracy: true, timeout: 12000, maximumAge: 0 },
      );
    },

    resolveClubPoint(club) {
      const latitude = toNumber(
        club?.latitude ?? club?.lat ?? club?.map_latitude ?? club?.geo_latitude ?? club?.location?.latitude ?? club?.geo?.latitude,
        null,
      );
      const longitude = toNumber(
        club?.longitude ?? club?.lng ?? club?.lon ?? club?.map_longitude ?? club?.geo_longitude ?? club?.location?.longitude ?? club?.geo?.longitude,
        null,
      );

      if (latitude !== null && longitude !== null) {
        return {
          latitude: clamp(latitude, MIN_LATITUDE, MAX_LATITUDE),
          longitude: normalizeLongitude(longitude),
        };
      }

      const fallback = CITY_COORDINATES[String(club?.city || '').trim()];
      return fallback || null;
    },

    project(latitude, longitude, zoom = this.zoom) {
      const level = clamp(Math.round(zoom), this.minZoom, this.maxZoom);
      const lat = clamp(Number(latitude || 0), MIN_LATITUDE, MAX_LATITUDE);
      const lng = normalizeLongitude(longitude);
      const sin = Math.sin((lat * Math.PI) / 180);
      const size = TILE_SIZE * (2 ** level);

      return {
        x: ((lng + 180) / 360) * size,
        y: (0.5 - Math.log((1 + sin) / (1 - sin)) / (4 * Math.PI)) * size,
      };
    },

    unproject(x, y, zoom = this.zoom) {
      const level = clamp(Math.round(zoom), this.minZoom, this.maxZoom);
      const size = TILE_SIZE * (2 ** level);

      return {
        longitude: normalizeLongitude((x / size) * 360 - 180),
        latitude: clamp(
          (180 / Math.PI) * Math.atan(Math.sinh(Math.PI * (1 - (2 * y) / size))),
          MIN_LATITUDE,
          MAX_LATITUDE,
        ),
      };
    },

    getMarkerGroupKey(point) {
      return `${Number(point.latitude).toFixed(5)}:${Number(point.longitude).toFixed(5)}`;
    },

    getMarkerSpreadOffset(index, total) {
      if (total <= 1) return { x: 0, y: 0 };
      if (total === 2) {
        return index === 0 ? { x: -18, y: 0 } : { x: 18, y: 0 };
      }

      const radius = Math.min(30, 16 + total * 3);
      const angle = ((Math.PI * 2) / total) * index - Math.PI / 2;

      return {
        x: Math.cos(angle) * radius,
        y: Math.sin(angle) * radius,
      };
    },

    getRelativeTouchPoint(touches) {
      const rect = this.$refs.mapViewport?.getBoundingClientRect();
      if (!rect) return { x: 0, y: 0 };

      if (touches.length === 1) {
        return {
          x: touches[0].clientX - rect.left,
          y: touches[0].clientY - rect.top,
        };
      }

      return {
        x: ((touches[0].clientX + touches[1].clientX) / 2) - rect.left,
        y: ((touches[0].clientY + touches[1].clientY) / 2) - rect.top,
      };
    },

    getTouchDistance(touches) {
      if (touches.length < 2) return 0;
      const dx = touches[0].clientX - touches[1].clientX;
      const dy = touches[0].clientY - touches[1].clientY;
      return Math.hypot(dx, dy);
    },

    startMapDrag(x, y) {
      this.isDragging = true;
      this.dragStart = {
        x,
        y,
        center: this.project(this.centerLatitude, this.centerLongitude, this.zoom),
      };
    },

    applyZoomAroundPoint(nextZoom, point, anchorLatLng = null) {
      const zoom = clamp(nextZoom, this.minZoom, this.maxZoom);
      if (zoom === this.zoom && !anchorLatLng) return;

      const anchor = anchorLatLng || (() => {
        const currentCenter = this.project(this.centerLatitude, this.centerLongitude, this.zoom);
        const world = {
          x: currentCenter.x - this.mapWidth / 2 + point.x,
          y: currentCenter.y - this.mapHeight / 2 + point.y,
        };
        return this.unproject(world.x, world.y, this.zoom);
      })();

      const anchorWorldAtNewZoom = this.project(anchor.latitude, anchor.longitude, zoom);
      const desiredCenterWorld = {
        x: anchorWorldAtNewZoom.x - point.x + this.mapWidth / 2,
        y: anchorWorldAtNewZoom.y - point.y + this.mapHeight / 2,
      };
      const nextCenter = this.unproject(desiredCenterWorld.x, desiredCenterWorld.y, zoom);

      this.centerLatitude = nextCenter.latitude;
      this.centerLongitude = nextCenter.longitude;
      this.zoom = zoom;
    },

    onMapTouchStart(event) {
      if (this.sheetDragging) return;

      this.touchActive = true;

      if (event.touches.length >= 2) {
        event.preventDefault();

        const midpoint = this.getRelativeTouchPoint(event.touches);
        const centerWorld = this.project(this.centerLatitude, this.centerLongitude, this.zoom);
        const anchorWorld = {
          x: centerWorld.x - this.mapWidth / 2 + midpoint.x,
          y: centerWorld.y - this.mapHeight / 2 + midpoint.y,
        };

        this.touchGesture = {
          mode: 'pinch',
          lastDistance: this.getTouchDistance(event.touches),
          anchorLatLng: this.unproject(anchorWorld.x, anchorWorld.y, this.zoom),
        };

        this.isDragging = false;
        this.dragStart = null;
        return;
      }

      const interactiveTarget = event.target.closest('button, input, textarea, a');
      if (interactiveTarget) {
        this.touchGesture = null;
        return;
      }

      const touch = event.touches[0];
      if (!touch) return;

      event.preventDefault();
      this.touchGesture = { mode: 'drag' };
      this.startMapDrag(touch.clientX, touch.clientY);
    },

    onGlobalTouchMove(event) {
      if (this.sheetDragging) {
        event.preventDefault();

        const touch = event.touches?.[0];
        if (!touch) return;

        const delta = this.sheetDragStartY - touch.clientY;
        const nextHeight = this.sheetStartHeightPx + delta;

        if (Math.abs(delta) > 4) {
          this.sheetDragMoved = true;
        }

        if (!this.sheetOpened && delta < -10) {
          this.sheetHeightPx = this.sheetMinHeightPx;
        } else {
          this.sheetOpened = true;
          this.sheetHeightPx = clamp(nextHeight, this.sheetMinHeightPx, this.sheetMaxHeightPx);
        }

        return;
      }

      if (!this.touchActive || !this.touchGesture) return;

      if (this.touchGesture.mode === 'pinch' && event.touches.length >= 2) {
        event.preventDefault();

        const distance = this.getTouchDistance(event.touches);
        const midpoint = this.getRelativeTouchPoint(event.touches);
        const diff = distance - this.touchGesture.lastDistance;

        if (Math.abs(diff) >= 14) {
          const nextZoom = clamp(Math.round(this.zoom) + (diff > 0 ? 1 : -1), this.minZoom, this.maxZoom);
          this.applyZoomAroundPoint(nextZoom, midpoint, this.touchGesture.anchorLatLng);
          this.touchGesture.lastDistance = distance;
        }

        return;
      }

      if (this.touchGesture.mode === 'drag' && event.touches.length === 1 && this.dragStart) {
        event.preventDefault();

        const touch = event.touches[0];
        const dx = touch.clientX - this.dragStart.x;
        const dy = touch.clientY - this.dragStart.y;
        const nextWorldPoint = {
          x: this.dragStart.center.x - dx,
          y: this.dragStart.center.y - dy,
        };
        const nextCenter = this.unproject(nextWorldPoint.x, nextWorldPoint.y, this.zoom);

        this.centerLatitude = nextCenter.latitude;
        this.centerLongitude = nextCenter.longitude;
      }
    },

    onGlobalTouchEnd(event) {
      if (this.sheetDragging) {
        this.finalizeSheetDrag();

        if (!event.touches || !event.touches.length) {
          this.touchActive = false;
        }
        return;
      }

      if (!event.touches || !event.touches.length) {
        this.touchActive = false;
        this.touchGesture = null;
        this.isDragging = false;
        this.dragStart = null;
        return;
      }

      if (event.touches.length === 1 && this.touchGesture?.mode === 'pinch') {
        const touch = event.touches[0];
        this.touchGesture = { mode: 'drag' };
        this.startMapDrag(touch.clientX, touch.clientY);
      }
    },

    onMapPointerDown(event) {
      if (this.touchActive) return;

      const interactiveTarget = event.target.closest('button, input, textarea, a');
      if (interactiveTarget) return;

      this.startMapDrag(event.clientX, event.clientY);
    },

    onGlobalPointerMove(event) {
      if (this.touchActive) return;

      if (this.sheetDragging) {
        const delta = this.sheetDragStartY - event.clientY;
        const nextHeight = this.sheetStartHeightPx + delta;

        if (Math.abs(delta) > 4) {
          this.sheetDragMoved = true;
        }

        if (!this.sheetOpened && delta < -10) {
          this.sheetHeightPx = this.sheetMinHeightPx;
        } else {
          this.sheetOpened = true;
          this.sheetHeightPx = clamp(nextHeight, this.sheetMinHeightPx, this.sheetMaxHeightPx);
        }

        return;
      }

      if (!this.isDragging || !this.dragStart) return;

      const dx = event.clientX - this.dragStart.x;
      const dy = event.clientY - this.dragStart.y;
      const nextWorldPoint = {
        x: this.dragStart.center.x - dx,
        y: this.dragStart.center.y - dy,
      };
      const nextCenter = this.unproject(nextWorldPoint.x, nextWorldPoint.y, this.zoom);

      this.centerLatitude = nextCenter.latitude;
      this.centerLongitude = nextCenter.longitude;
    },

    onGlobalPointerUp() {
      if (this.touchActive) return;

      if (this.sheetDragging) {
        this.finalizeSheetDrag();
        return;
      }

      this.isDragging = false;
      this.dragStart = null;
    },

    onMapWheel(event) {
      const rect = this.$refs.mapViewport?.getBoundingClientRect();
      const point = rect
        ? { x: event.clientX - rect.left, y: event.clientY - rect.top }
        : { x: this.mapWidth / 2, y: this.mapHeight / 2 };

      const delta = event.deltaY > 0 ? -1 : 1;
      const nextZoom = clamp(Math.round(this.zoom) + delta, this.minZoom, this.maxZoom);
      this.applyZoomAroundPoint(nextZoom, point);
    },

    zoomIn() {
      this.applyZoomAroundPoint(
        clamp(Math.round(this.zoom) + 1, this.minZoom, this.maxZoom),
        { x: this.mapWidth / 2, y: this.mapHeight / 2 },
      );
    },

    zoomOut() {
      this.applyZoomAroundPoint(
        clamp(Math.round(this.zoom) - 1, this.minZoom, this.maxZoom),
        { x: this.mapWidth / 2, y: this.mapHeight / 2 },
      );
    },

    fitToClubs(clubs) {
      if (!clubs.length) {
        this.centerLatitude = 55.7558;
        this.centerLongitude = 37.6173;
        this.zoom = 4;
        return;
      }

      if (clubs.length === 1) {
        const point = this.resolveClubPoint(clubs[0]);
        if (!point) return;

        this.centerLatitude = point.latitude;
        this.centerLongitude = point.longitude;
        this.zoom = 13;
        return;
      }

      const width = Math.max(this.mapWidth, 320);
      const height = Math.max(this.mapHeight, 320);
      const padding = 110;
      let fitted = null;

      for (let zoom = this.maxZoom; zoom >= this.minZoom; zoom -= 1) {
        const points = clubs
          .map((club) => this.resolveClubPoint(club))
          .filter(Boolean)
          .map((point) => this.project(point.latitude, point.longitude, zoom));

        if (!points.length) continue;

        const minX = Math.min(...points.map((point) => point.x));
        const maxX = Math.max(...points.map((point) => point.x));
        const minY = Math.min(...points.map((point) => point.y));
        const maxY = Math.max(...points.map((point) => point.y));

        if ((maxX - minX) <= Math.max(width - padding * 2, 60)
          && (maxY - minY) <= Math.max(height - padding * 2, 60)) {
          fitted = {
            zoom,
            center: this.unproject((minX + maxX) / 2, (minY + maxY) / 2, zoom),
          };
          break;
        }
      }

      if (!fitted) {
        const points = clubs
          .map((club) => this.resolveClubPoint(club))
          .filter(Boolean)
          .map((point) => this.project(point.latitude, point.longitude, this.minZoom));

        if (!points.length) return;

        fitted = {
          zoom: this.minZoom,
          center: this.unproject(
            (Math.min(...points.map((point) => point.x)) + Math.max(...points.map((point) => point.x))) / 2,
            (Math.min(...points.map((point) => point.y)) + Math.max(...points.map((point) => point.y))) / 2,
            this.minZoom,
          ),
        };
      }

      this.centerLatitude = fitted.center.latitude;
      this.centerLongitude = fitted.center.longitude;
      this.zoom = fitted.zoom;
    },

    focusOnClub(club, zoom = 15) {
      const point = this.resolveClubPoint(club);
      if (!point) return;

      this.centerLatitude = point.latitude;
      this.centerLongitude = point.longitude;
      this.zoom = clamp(zoom, this.minZoom, this.maxZoom);
    },

    openMarker(marker) {
      this.geolocationError = '';

      if (marker.type === 'cluster') {
        this.selectedClubId = null;
        this.centerLatitude = marker.latitude;
        this.centerLongitude = marker.longitude;
        this.zoom = clamp(Math.round(this.zoom) + 2, this.minZoom, this.maxZoom);
        return;
      }

      this.selectClubOnMap(marker.club, {
        focus: true,
        closeSearch: true,
      });
    },

    async selectClubOnMap(club, { focus = true, closeSearch = false } = {}) {
      if (!club?.id) return;

      const clubId = Number(club.id);
      this.selectedClubId = clubId;
      this.collapseSheet();

      if (focus) {
        this.focusOnClub(club, Math.max(14, Math.round(this.zoom)));
      }

      if (closeSearch) {
        this.showSearchPanel = false;
      }

      if (clubId === Number(this.currentClub?.id || 0)) {
        await this.$store.dispatch('booking/initBooking', clubId);
        return;
      }

      const result = await this.$store.dispatch('clubs/selectClub', clubId);
      if (!result?.ok) {
        return;
      }

      await Promise.all([
        this.$store.dispatch('clubs/fetchClubById', clubId),
        this.$store.dispatch('booking/initBooking', clubId),
      ]);
    },
    openSelectedClubPage() {
      if (!this.selectedClub?.id) return;
      this.$router.push(`/clubs/${this.selectedClub.id}`);
    },

    isCurrentClub(club) {
      return Number(this.currentClub?.id || 0) === Number(club?.id || 0);
    },

    displayRating(club) {
      const rating = Number(club?.rating || 0);
      if (!Number.isFinite(rating) || rating <= 0) return '';
      return rating.toFixed(1);
    },

    money(value) {
      const amount = Number(value || 0);
      return `${new Intl.NumberFormat('ru-RU').format(amount)} ₽`;
    },

    clubMonogram(club) {
      const name = String(club?.name || '').trim();
      if (!name) return 'CC';

      const parts = name.split(/\s+/).filter(Boolean);
      if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();

      return `${parts[0][0] || ''}${parts[1][0] || ''}`.toUpperCase();
    },

    toggleCityPanel() {
      this.showCityPanel = !this.showCityPanel;
      this.showSearchPanel = false;

      if (this.showCityPanel) {
        this.$nextTick(() => {
          this.$refs.citySearchInput?.focus();
        });
      }
    },

    closeCityPanel() {
      this.showCityPanel = false;
      this.citySearchQuery = '';
    },

    selectCity(city) {
      this.selectedCity = city;
    },

    toggleSearchPanel() {
      this.showSearchPanel = !this.showSearchPanel;
      this.closeCityPanel();

      if (this.showSearchPanel) {
        this.$nextTick(() => {
          this.$refs.searchInput?.focus();
        });
      }
    },
  },
};
</script>

<style scoped src="./ClubsView.css"></style>